import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import type { SelectChangeEvent } from "@mui/material/Select";
import HeatmapIcon from "@mui/icons-material/Whatshot";
import LayersIcon from "@mui/icons-material/Layers";
import SensorsIcon from "@mui/icons-material/SettingsInputAntenna";
import TracksIcon from "@mui/icons-material/Timeline";
import TrackPointsIcon from "@mui/icons-material/ScatterPlot";
import { SelectWithTooltip } from "@/shared/ui/select-with-tooltip";

/** Suppresses the default selected-item background highlight so the checkbox alone conveys selection. */
const NO_SELECTED_HIGHLIGHT_SX = {
  "&.Mui-selected": { backgroundColor: "transparent" },
  "&.Mui-selected:hover": { backgroundColor: "action.hover" },
};

const LAYERS_SELECT_SX = {
  width: 160,
  flexShrink: 0,
  "& .MuiSelect-select": {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
};

interface LayerOption {
  id: string;
  label: string;
  icon: typeof HeatmapIcon;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

interface LayersSelectProps {
  showLabel?: boolean;
  showHeatmap: boolean;
  onShowHeatmapChange: (showHeatmap: boolean) => void;
  showSensors: boolean;
  onShowSensorsChange: (showSensors: boolean) => void;
  showDetections: boolean;
  onShowDetectionsChange: (showDetections: boolean) => void;
  showTracks: boolean;
  onShowTracksChange: (showTracks: boolean) => void;
}

/** Shared layer visibility control for facility map views. */
export function LayersSelect({
  showLabel = false,
  showHeatmap,
  onShowHeatmapChange,
  showSensors,
  onShowSensorsChange,
  showDetections,
  onShowDetectionsChange,
  showTracks,
  onShowTracksChange,
}: LayersSelectProps) {
  const layerOptions: LayerOption[] = [
    {
      id: "heatmap",
      label: "Heatmap",
      icon: HeatmapIcon,
      checked: showHeatmap,
      onChange: onShowHeatmapChange,
    },
    {
      id: "detections",
      label: "Detections",
      icon: TrackPointsIcon,
      checked: showDetections,
      onChange: onShowDetectionsChange,
    },
    {
      id: "tracks",
      label: "Tracks",
      icon: TracksIcon,
      checked: showTracks,
      onChange: onShowTracksChange,
    },
    {
      id: "sensors",
      label: "Sensors",
      icon: SensorsIcon,
      checked: showSensors,
      onChange: onShowSensorsChange,
    },
  ];

  const selectedLayerIds = layerOptions
    .filter((option) => option.checked)
    .map((option) => option.id);

  function handleLayerSelectionChange(event: SelectChangeEvent<string[]>) {
    const { value } = event.target;
    const nextLayerIds = typeof value === "string" ? value.split(",") : value;

    layerOptions.forEach((option) => option.onChange(nextLayerIds.includes(option.id)));
  }

  const labelId = "layers-filter-label";
  const select = (
    <SelectWithTooltip
      tooltipTitle="Layers"
      aria-label="Layers"
      labelId={showLabel ? labelId : undefined}
      multiple
      displayEmpty
      size="small"
      value={selectedLayerIds}
      onChange={handleLayerSelectionChange}
      startAdornment={<LayersIcon sx={{ fontSize: "0.875rem", mr: 1, color: "text.secondary" }} />}
      sx={{ ...LAYERS_SELECT_SX, ...(showLabel ? { width: 180 } : {}) }}
      renderValue={(selected) => {
        if (selected.length === 0) return "No Layers";
        if (selected.length === layerOptions.length) return "All Layers";

        return layerOptions
          .filter((option) => selected.includes(option.id))
          .map((option) => option.label)
          .join(", ");
      }}
    >
      {layerOptions.map((option) => (
        <MenuItem key={option.id} value={option.id} sx={NO_SELECTED_HIGHLIGHT_SX}>
          <Checkbox checked={option.checked} size="small" />
          <option.icon sx={{ mr: 1, fontSize: "1rem", color: "text.secondary" }} />
          <ListItemText primary={option.label} />
        </MenuItem>
      ))}
    </SelectWithTooltip>
  );

  if (showLabel) {
    return (
      <FormControl sx={{ minWidth: 180 }}>
        <FormLabel
          id={labelId}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            mb: 0.5,
            fontSize: "0.625rem",
            fontWeight: 700,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: "text.secondary",
            "&.Mui-focused": { color: "text.secondary" },
          }}
        >
          <Box component="span" aria-hidden="true" sx={{ display: "flex", fontSize: "0.875rem" }}>
            <LayersIcon sx={{ fontSize: "0.875rem" }} />
          </Box>
          Layers
        </FormLabel>
        {select}
      </FormControl>
    );
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", minWidth: "max-content" }}>{select}</Box>
  );
}
